import { Trans, useLingui } from "@lingui/react/macro";
import { patientKeys, updatePhoneNumber } from "@/api/Patients";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import InputField from "@medoma/components/InputField/InputField";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import {
  localizePhoneNumber,
  standardizePhoneNumber,
  type LocalizedPhoneNumber,
  type RawPhoneNumber,
} from "@medoma/models/phoneNumbers";
import { useUserProfile } from "@/api/Users";

export const EditPhoneNumber = ({
  currentPhoneNumber,
  onSuccess,
  patientId,
}: {
  currentPhoneNumber: RawPhoneNumber | null | undefined;
  onSuccess: () => void;
  patientId: string;
}) => {
  const { t } = useLingui();
  const { countryCode } = useUserProfile();

  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      phoneNumber: currentPhoneNumber
        ? localizePhoneNumber({ countryCode, phoneNumber: currentPhoneNumber })
        : undefined,
    },
  });

  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (phoneNumber: LocalizedPhoneNumber) => {
      return updatePhoneNumber(
        patientId,
        standardizePhoneNumber({ countryCode, phoneNumber }),
      );
    },
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: patientKeys.detail(patientId),
      });
      onSuccess();
    },
  });

  return (
    <Form
      onSubmit={handleSubmit((formData) => {
        if (!formData.phoneNumber) {
          return;
        }
        mutate(formData.phoneNumber);
      })}
    >
      {isPending || isSuccess ? (
        <Loading message={t`Ändrar telefonnumret`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : null}
          <Form.Row>
            <InputField
              label={t`Telefonnummer`}
              showOptionalLabel={false}
              errorMessage={errors.phoneNumber?.message}
              {...register("phoneNumber", {
                required: {
                  value: true,
                  message: t`Ange telefonnummer`,
                },
                pattern: {
                  value: /^([0-9]|\s)+$/,
                  message: t`Telefonnumret får bara innehålla siffror`,
                },
              })}
              outerWidth="fit"
            />
          </Form.Row>
          <FilledButton type="submit">
            <Trans>Spara ändringar</Trans>
          </FilledButton>
        </>
      )}
    </Form>
  );
};
